/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.

==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return {x: 0, y: 0}
  },
  routes: [

    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // User Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dashboard'
        },
        {
          path: '/dashboard',
          name: 'dashboard',
          component: () => import('./views/user/Dashboard.vue'),
          meta: {
            rule: 'visitor',
            authRequired: true
          }
        },
        {
          path: '/select-course',
          name: 'course-selector',
          component: () => import('./views/course/course-selector/CourseSelector.vue'),
          meta: {
            rule: 'user',
            pageTitle: 'Course Selector',
            authRequired: true
          }
        },
        {
          path: '/course/:courseId',
          name: 'course-view',
          component: () => import('./views/course/course-view/CourseView'),
          meta: {
            rule: 'user',
            authRequired: true
          }
        },
        // =============================================================================
        // Learn & Simulation Routes
        // =============================================================================
        {
          path: '/practice',
          name: 'simulation-practice',
          component: () => import('./views/learn/pathway/SimulationPractice.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Simulations'},
              {title: 'Practice', active: true}
            ],
            rule: 'user',
            pageTitle: 'Course Pathway',
            authRequired: true
          }
        },
        {
          path: '/simulation/search',
          name: 'simulation-search',
          component: () => import('./views/learn/search/SimulationSearch.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Simulations'},
              {title: 'Selector', active: true}
            ],
            rule: 'user',
            pageTitle: 'Simulation Selector',
            authRequired: true
          }
        },
        // =============================================================================
        // Legacy Simulation Routes
        // =============================================================================
        {
          path: '/simulation/l/:simulationId',
          name: 'legacy-simulation-starter',
          component: () => import('./views/simulations/legacy/SimulationDetails.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Simulation Selector', url: '/simulation/search'},
              {title: 'View', active: true}
            ],
            rule: 'user',
            pageTitle: 'Simulation Starter',
            authRequired: true
          }
        },
        {
          path: '/simulation/l/:simulationId/play',
          name: 'legacy-simulation-play',
          component: () => import('./views/simulations/legacy/SimulationRenderer.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Simulation Selector', url: '/simulation/search'},
              {title: 'Play', active: true}
            ],
            rule: 'user',
            pageTitle: 'Simulation',
            authRequired: true
          }
        },
        // =============================================================================
        // Course Management Routes
        // =============================================================================
        {
          path: '/a/courses',
          name: 'management-list-course',
          component: () => import('./views/management/courses/course-list/ListCourses.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Courses List',
            authRequired: true
          }
        },
        {
          path: '/a/courses/new',
          ame: 'management-create-course',
          component: () => import('./views/management/courses/course-create/CreateCourse.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'New', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'New Course',
            authRequired: true
          }
        },
        {
          path: '/a/courses/:courseId',
          name: 'management-view-course',
          component: () => import('./views/management/courses/course-view/CourseView.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Course',
            authRequired: true
          }
        },
        {
          path: '/a/courses/:courseId/subscriptions',
          name: 'management-view-course-subscriptions',
          component: () => import('./views/management/courses/course-view/subscription-list/ListSubscriptions.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'Subscriptions', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Course Subscriptions',
            authRequired: true
          }
        },
        {
          path: '/a/courses/:courseId/edit',
          name: 'management-edit-course',
          component: () => import('./views/management/courses/course-edit/EditCourse.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Courses', url: '/a/courses'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Course',
            authRequired: true
          }
        },
        // =============================================================================
        // Group Management Routes
        // =============================================================================
        {
          path: '/a/groups',
          name: 'management-list-group',
          component: () => import('./views/management/groups/group-list/ListGroups.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Groups List',
            authRequired: true
          }
        },
        {
          path: '/a/groups/new',
          ame: 'management-create-group',
          component: () => import('./views/management/groups/group-create/CreateGroup.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'New', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'New Group',
            authRequired: true
          }
        },
        {
          path: '/a/groups/:groupId',
          name: 'management-view-group',
          component: () => import('./views/management/groups/group-view/GroupView.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Group',
            authRequired: true
          }
        },
        {
          path: '/a/groups/:groupId/subscriptions',
          name: 'management-view-group-subscriptions',
          component: () => import('./views/management/groups/group-view/GroupView'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'Subscriptions', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Group Subscriptions',
            authRequired: true
          }
        },
        {
          path: '/a/groups/:groupId/edit',
          name: 'management-edit-group',
          component: () => import('./views/management/groups/group-edit/EditGroup.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Groups', url: '/a/groups'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Group',
            authRequired: true
          }
        },
        // =============================================================================
        // User Management Routes
        // =============================================================================
        {
          path: '/a/users',
          name: 'management-list-user',
          component: () => import('./views/management/users/user-list/ListUser'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'User List',
            authRequired: true
          }
        },
        {
          path: '/a/users/upload',
          name: 'management-upload-user',
          component: () => import('./views/management/users/user-create/UploadUsers'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', url: '/a/users'},
              {title: 'Upload', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Upload Users',
            authRequired: true
          }
        },
        {
          path: '/a/users/:userId',
          name: 'management-view-user',
          component: () => import('./views/management/users/user-view/UserView'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', url: '/a/users'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View User',
            authRequired: true
          }
        },
        {
          path: '/a/users/:userId/progress/:courseId',
          name: 'management-view-user-course-progress',
          component: () => import('./views/management/users/user-view/UserViewCourseProgress'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Users', url: '/a/users'},
              {title: 'View User Course Progress', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View User Course Progress',
            authRequired: true
          }
        },
        // =============================================================================
        // Simulation Management Routes
        // =============================================================================
        {
          path: '/a/simulations',
          name: 'management-list-simulation',
          component: () => import('./views/management/simulations/simulation-list/ListSimulations.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Simulations', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Simulations List'
          }
        },
        {
          path: '/a/simulations/:simulationId',
          name: 'management-view-simulation',
          component: () => import('./views/management/simulations/simulation-view/SimulationView'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Simulations', url: '/a/simulations'},
              {title: 'View', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'View Simulation',
            authRequired: true
          }
        },
        {
          path: '/a/simulations/:simulationId/edit',
          name: 'management-edit-simulation',
          component: () => import('./views/management/simulations/simulation-edit/EditSimulation.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Proctor'},
              {title: 'Simulations', url: '/a/simulations'},
              {title: 'Edit', active: true}
            ],
            rule: 'proctor',
            pageTitle: 'Edit Simulation',
            authRequired: true
          }
        },
        // =============================================================================
        // Retailer Management Routes
        // =============================================================================
        /*
        {
          path: '/a/retailers',
          name: 'management-list-retailer',
          component: () => import('./views/management/retailers/retailer-list/ListRetailers'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Retailers', active: true}
            ],
            rule: 'admin',
            pageTitle: 'Retailers List'
          }
        },
        {
          path: '/a/retailers/:retailerId',
          name: 'management-view-retailer',
          component: () => import('./views/management/retailers/retailer-view/RetailerView'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Retailers', url: '/a/retailers'},
              {title: 'View', active: true}
            ],
            rule: 'admin',
            pageTitle: 'View Retailer'
          }
        },
        {
          path: '/a/retailers/:retailerId/keys',
          name: 'management-keys-retailer',
          component: () => import('./views/management/retailers/retailer-keys/ListRetailerKeys.vue'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Retailers', url: '/a/retailers'},
              {title: 'Keys', active: true}
            ],
            rule: 'admin',
            pageTitle: 'Retailer Key Portal'
          }
        },
        {
          path: '/a/retailers/:retailerId/keys/generate',
          name: 'management-keys-retailer-generate',
          component: () => import('./views/management/retailers/retailer-view/RetailerGenerateKeys'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Retailers', url: '/a/retailers'},
              {title: 'Generate Keys', active: true}
            ],
            rule: 'admin',
            pageTitle: 'Retailer Key Generation'
          }
        },
         */
        // =============================================================================
        // Subscription Management Routes
        // =============================================================================
        {
          path: '/a/subscriptions',
          name: 'management-list-subscription',
          component: () => import('./views/management/subscriptions/subscription-list/ListSubscriptions'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Subscriptions', active: true}
            ],
            rule: 'admin',
            pageTitle: 'Subscriptions List',
            authRequired: true
          }
        },
        {
          path: '/a/subscriptions/:subscriptionsId',
          name: 'management-view-subscription',
          component: () => import('./views/management/subscriptions/subscription-list/ListSubscriptions'),
          meta: {
            breadcrumb: [
              {title: 'Home', url: '/'},
              {title: 'Management'},
              {title: 'Subscriptions', url: '/a/subscriptions'},
              {title: 'View', active: true}
            ],
            rule: 'admin',
            pageTitle: 'View Subscription',
            authRequired: false
          }
        }
      ]
    },

    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/login',
          name: 'login',
          component: () => import('./views/login/Login.vue'),
          meta: {
            rule: 'visitor',
            authRequired: false
          }
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          meta: {
            rule: 'visitor',
            authRequired: true
          },
          component: () => import('./views/utils/Error404.vue')
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ]
})

router.beforeEach((to, from, next) => {

  //if (to.path === '/authorize') {
  // if (localStorage.getItem('accessToken') && localStorage.getItem('refreshToken')) {
  // }
  // return next()
  // }

  if (to.meta.authRequired) {

    if (!localStorage.getItem('refreshToken')) {
      return router.push({path: '/login'})
    }

    if (!localStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
      return router.push({path: '/login'})
    }
  }

  return next()

})

router.afterEach((to) => {
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
  if (!to.path.includes('/login')) {
    store.dispatch('auth/refreshUserObject')
      .catch(() => {})

    store.dispatch('auth/refreshCourseObject')
      .catch(() => {})

  }
})
export default router
